import React, { useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserCog,
  BellRing,
  MessageSquare,
  Database,
  FileText,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../contexts/SettingsContext';
import UserSettingsSection from '../../components/Settings/UserSettingsSection';
import NotificationSettingsSection from '../../components/Settings/NotificationSettingsSection';
import ChatSettingsSection from '../../components/Settings/ChatSettingsSection';
import DataSettingsSection from '../../components/Settings/DataSettingsSection';
import GeneralSettingsSection from '../../components/Settings/GeneralSettingsSection';

const contentVariants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
};

const VALID_SETTINGS_TABS = new Set(['user', 'notifications', 'chat', 'data', 'general']);

const SettingsPage = () => {
  const { t } = useTranslation('settings');
  const { activeTab, setActiveTab } = useSettings();

  const tabs = useMemo(
    () => [
      { id: 'user', label: t('sidebar.menu.user.label'), icon: UserCog },
      { id: 'notifications', label: t('sidebar.menu.notifications.label'), icon: BellRing },
      { id: 'chat', label: t('sidebar.menu.chat.label'), icon: MessageSquare },
      { id: 'data', label: t('sidebar.menu.data.label'), icon: Database },
      { id: 'general', label: t('sidebar.menu.general.label'), icon: FileText },
    ],
    [t]
  );

  // Legacy tabs (theme / license / updates) — fall back to user so Settings never blanks
  useEffect(() => {
    if (!VALID_SETTINGS_TABS.has(activeTab)) {
      setActiveTab('user');
    }
  }, [activeTab, setActiveTab]);

  const safeTab = VALID_SETTINGS_TABS.has(activeTab) ? activeTab : 'user';

  const renderContent = () => {
    switch (safeTab) {
      case 'user':
        return <UserSettingsSection />;
      case 'notifications':
        return <NotificationSettingsSection />;
      case 'chat':
        return <ChatSettingsSection />;
      case 'data':
        return <DataSettingsSection />;
      case 'general':
        return <GeneralSettingsSection />;
      default:
        return <UserSettingsSection />;
    }
  };

  return (
    <div className="h-full flex bg-white dark:bg-[#080e1a]">
      <aside
        className="w-64 flex-shrink-0 border-r border-slate-200 dark:border-slate-800 p-6 h-full overflow-y-auto bg-white dark:bg-[#080e1a]"
        aria-label={t('settingsPage.tabsAriaLabel')}
      >
        <div className="mb-4">
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-200">{t('settingsPage.title')}</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {t('settingsPage.subtitle')}
          </p>
        </div>
        <nav className="flex flex-col gap-0.5">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = safeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors
                  ${isActive
                    ? 'bg-blue-50 text-blue-700 font-medium dark:bg-blue-900/20 dark:text-blue-400'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-[var(--color-bg-muted)]'
                  }
                `}
              >
                <Icon
                  className="w-5 h-5 flex-shrink-0"
                  strokeWidth={isActive ? 2.2 : 2}
                />
                <span className="truncate">{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      <div className="flex-1 min-w-0 h-full overflow-y-auto bg-white dark:bg-[#080e1a]">
        <div className="max-w-4xl p-10 h-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={safeTab}
              variants={contentVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
